import '../styles/StreamingPlatforms.css';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import axios from 'axios';
import SearchContent from '../components/movies/searchContent';
import MovieRoulette from '../components/movies/movieRoulette';
import Favorites from "../components/movies/favorites";
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import NetflixSVG from '../assets/netflix.svg';
import AmazonPrimeSVG from '../assets/amazon-prime.svg';
import DisneyPlusSVG from '../assets/disney-plus.svg';
import AppleTVSVG from '../assets/apple-tv.svg';

const Movies = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const initialTab = parseInt(queryParams.get('tab')) || 0;

    const [activeView, setActiveView] = useState(initialTab);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState([]);
    const [isIntersectionContent, setIsIntersectionContent] = useState(false);
    const [userRegion, setUserRegion] = useState('US');
    const [partnerRegion, setPartnerRegion] = useState(null);
    const [showRouletteDialog, setShowRouletteDialog] = useState(false);

    const platforms = [
        { image: NetflixSVG, id: "8", title: "Netflix" },
        { image: AmazonPrimeSVG, id: "9", title: "Amazon Prime Video" },
        { image: DisneyPlusSVG, id: "337", title: "Disney Plus" },
        { image: AppleTVSVG, id: "350", title: "Apple TV Plus" },
    ];

    const handlePlatformSelect = (platform) => {
        const platformPath = platform.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`/dashboard/dates/movies/${platformPath}`);
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;

        setLoading(true);
        try {
            const response = await axios.get(`https://api.frazza.online/search/multi?query=${encodeURIComponent(searchQuery)}`);
            const filteredResults = response.data.results.filter(item => item.media_type === 'movie' || item.media_type === 'tv');
            const resultsWithProviders = await Promise.all(
                filteredResults.map(async (item) => {
                    try {
                        const providersResponse = await axios.get(
                            `https://api.frazza.online/content/${item.media_type}/${item.id}/watch-providers`
                        );

                        const allProviders = providersResponse.data.results || {};

                        const availableOn = platforms.filter(platform => {
                            for (const [region, data] of Object.entries(allProviders)) {
                                const flatrate = data.flatrate || [];
                                const rent = data.rent || [];
                                const buy = data.buy || [];

                                const allServices = [...flatrate, ...rent, ...buy];
                                if (allServices.some(service => service.provider_id.toString() === platform.id)) {
                                    return true;
                                }
                            }
                            return false;
                        });

                        return {
                            ...item,
                            availableOn,
                            hasProviders: availableOn.length > 0
                        };
                    } catch (error) {
                        console.error("Error fetching providers:", error);
                        return {
                            ...item,
                            availableOn: [],
                            hasProviders: false
                        };
                    }
                })
            );

            setSearchResults(resultsWithProviders);
        } catch (error) {
            console.error("Search error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setActiveView(initialTab);
    }, [initialTab]);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await axios.get("https://api.frazza.online/content");
                const fetchedContent = response.data;

                if (partnerRegion) {
                    const intersectionContent = fetchedContent.filter((item) =>
                        item.availableRegions.includes(userRegion) &&
                        item.availableRegions.includes(partnerRegion)
                    );
                    setContent(intersectionContent);
                    setIsIntersectionContent(true);
                } else {
                    const userContent = fetchedContent.filter((item) =>
                        item.availableRegions.includes(userRegion)
                    );
                    setContent(userContent);
                    setIsIntersectionContent(false);
                }
            } catch (error) {
                console.error("Error fetching content:", error);
            }
        };

        fetchContent();
    }, [userRegion, partnerRegion]);

    const handleTabChange = (e) => {
        setActiveView(e.index);
        navigate(`?tab=${e.index}`);
    };

    const onHide = () => {
        setShowRouletteDialog(false);
    };

    return (
        <div className="movies-container">
            <h1>Frazzinema</h1>
            <TabView activeIndex={activeView} onTabChange={handleTabChange} className="movies-tabview">
                <TabPanel header="All Platforms" className="all-platforms-tab">
                    <div className="platforms-content">
                        <div className="platforms-grid">
                            {platforms.map((platform, index) => (
                                <img
                                    key={index}
                                    src={platform.image}
                                    alt={platform.title}
                                    className="platform-icon"
                                    onClick={() => handlePlatformSelect(platform)}
                                />
                            ))}
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header="Search Movies" className="search-movies-tab">
                    <SearchContent
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        handleSearch={handleSearch}
                        loading={loading}
                        searchResults={searchResults}
                        className="search-content"
                    />
                </TabPanel>

                <TabPanel header="My Watchlist" className="my-watchlist-tab">
                    <div className="watchlist-content">
                        <Favorites />
                    </div>
                </TabPanel>
            </TabView>

            <Button
                label="Movie Roulette"
                className="p-button-rounded p-button-success roulette-button"
                onClick={() => setShowRouletteDialog(true)}
            />

            <Dialog
                visible={showRouletteDialog}
                onHide={onHide}
                className="dialog-MovieRoulette frazza-dialog-lg"
                closable
                modal
                header="Movie Roulette"
            >
                <MovieRoulette
                    content={content}
                    isIntersectionContent={isIntersectionContent}
                    userRegion={userRegion}
                    partnerRegion={partnerRegion}
                    onClose={onHide}
                />
            </Dialog>
        </div>
    );
};

export default Movies;
